// services/permission.service.ts
import { Injectable } from '@angular/core';

export interface RoleConfiguration {
  roleName: string;
  specialty?: string;
  hasFullAccess: boolean;
  permissions: {
    allowedServiceRequestCategories: string[];
    allowedObservationCategories: string[];
    customPermissions: {
      canViewAllPatients: boolean;
      canOrderTests: boolean;
      canPrescribeMedications: boolean;
      canPerformProcedures: boolean;
    };
  };
}

export interface ParsedPermissions {
  roleSpecialties: { [roleName: string]: string };
  hasFullAccess: boolean;
  allowedServiceRequestCategories: string[];
  allowedObservationCategories: string[];
  customPermissions: {
    canViewAllPatients: boolean;
    canOrderTests: boolean;
    canPrescribeMedications: boolean;
    canPerformProcedures: boolean;
  };
  roleConfigurations: RoleConfiguration[];
}

@Injectable({
  providedIn: 'root'
})
export class PermissionService {

  /**
   * Parse RolePermissionsJson and merge permissions from all roles
   */
  parsePermissions(rolePermissionsJson?: string | null): ParsedPermissions | null {
    if (!rolePermissionsJson) {
      return null;
    }

    try {
      const roleConfigs: RoleConfiguration[] = JSON.parse(rolePermissionsJson);

      if (!roleConfigs || roleConfigs.length === 0) {
        return null;
      }

      const permissions: ParsedPermissions = {
        roleSpecialties: {},
        hasFullAccess: false,
        allowedServiceRequestCategories: [],
        allowedObservationCategories: [],
        customPermissions: {
          canViewAllPatients: false,
          canOrderTests: false,
          canPrescribeMedications: false,
          canPerformProcedures: false
        },
        roleConfigurations: roleConfigs
      };

      // Merge permissions from all roles
      for (const config of roleConfigs) {
        // Track specialties
        if (config.specialty) {
          permissions.roleSpecialties[config.roleName] = config.specialty;
        }

        // If ANY role has full access, user gets full access
        if (config.hasFullAccess) {
          permissions.hasFullAccess = true;
        }

        // Merge ServiceRequest categories
        if (config.permissions?.allowedServiceRequestCategories) {
          permissions.allowedServiceRequestCategories.push(
            ...config.permissions.allowedServiceRequestCategories
          );
        }

        // Merge Observation categories
        if (config.permissions?.allowedObservationCategories) {
          permissions.allowedObservationCategories.push(
            ...config.permissions.allowedObservationCategories
          );
        }

        // Merge custom permissions (OR logic)
        const custom = config.permissions?.customPermissions;
        if (custom) {
          permissions.customPermissions.canViewAllPatients ||= custom.canViewAllPatients;
          permissions.customPermissions.canOrderTests ||= custom.canOrderTests;
          permissions.customPermissions.canPrescribeMedications ||= custom.canPrescribeMedications;
          permissions.customPermissions.canPerformProcedures ||= custom.canPerformProcedures;
        }
      }

      // Remove duplicates
      permissions.allowedServiceRequestCategories = [
        ...new Set(permissions.allowedServiceRequestCategories)
      ];
      permissions.allowedObservationCategories = [
        ...new Set(permissions.allowedObservationCategories)
      ];

      return permissions;
    } catch (error) {
      console.error('Failed to parse RolePermissionsJson:', error);
      return null;
    }
  }

  /**
   * Check if user can access a specific ServiceRequest category
   */
  canAccessServiceRequestCategory(
    permissions: ParsedPermissions | null,
    category: string
  ): boolean {
    if (!permissions) return false;

    // Full access
    if (permissions.hasFullAccess) return true;

    // Empty list = full access (legacy behavior)
    if (permissions.allowedServiceRequestCategories.length === 0) return true;

    // Check specific category (case-insensitive)
    return permissions.allowedServiceRequestCategories.some(
      c => c.toLowerCase() === category.toLowerCase()
    );
  }

  /**
   * Check if user can access a specific Observation category
   */
  canAccessObservationCategory(
    permissions: ParsedPermissions | null,
    category: string
  ): boolean {
    if (!permissions) return false;

    if (permissions.hasFullAccess) return true;

    if (permissions.allowedObservationCategories.length === 0) return true;

    return permissions.allowedObservationCategories.some(
      c => c.toLowerCase() === category.toLowerCase()
    );
  }

  /**
   * Get user's specialty for a specific role
   */
  getSpecialty(
    permissions: ParsedPermissions | null,
    roleName: string = 'Doctor'
  ): string | undefined {
    return permissions?.roleSpecialties[roleName];
  }
}
